// W14 Dialog (BaseDialog: persistent, 560 px as Quasar caps it, 740 px for legacy's max-width="lg", radius 14 (a bare q-dialog's 4, B1321), title heading-sm bold + a round close on gray-50, body max
// 65dvh, footer right with outline Cancel left of the primary action, maximized on mobile), W15 SideSheet (UiSideCard
// slide-over), W16 DeleteDialog (UiDeleteDialog: 450 px, illustration, outline Cancel + negative Delete) and W18
// toasts (Quasar Notify at the bottom with a progress bar: error 800 ms, success 1000 ms, warning 4000 ms).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog as RadixDialog, Toast } from 'radix-ui';
import { backofficeMetrics } from '@radish/ui-tokens';
import { Button } from './button.tsx';

interface DialogBaseProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: number;
  readonly testId?: string | undefined;
  /**
   * Where focus lands when the dialog opens. Legacy q-dialogs move focus into the dialog (its close button) and focus no
   * field, so that is the default and any `autoFocus` inside is overridden. 'first-field' is the explicit opt-in for a
   * dialog whose legacy screen focuses its first input (coordinator decision 2026-10-12).
   */
  readonly initialFocus?: 'close' | 'first-field';
  /**
   * Escape leaves a dialog open by default, as legacy's persistent q-dialog / BaseDialog does (the close button and Cancel
   * still close it; coordinator, 2026-10-15). True is an explicit opt-in for a dialog that should close on Escape.
   */
  readonly closeOnEscape?: boolean;
  /**
   * Below 600 px every footer button takes an equal share of the row, as legacy's BaseButton `:full="domStore.isMobile"`
   * (B1286). Unset, the buttons keep their own width at the footer's ends.
   */
  readonly phoneFooterFill?: boolean;
  /**
   * BaseDialog's `max-width` (B1243). From 600 Quasar's minimized q-dialog caps every card at 560 (`.q-dialog__inner--minimized
   * > div { max-width: 560px }`); 'lg' is BaseDialog's `max-size-lg`, which lifts that to 740 from 800 only. Measured on legacy
   * GG LA: Create Modifier Set (lg) is 560 at 620 and 799, 740 at 800 and 1024; Add Banner and Add Special Hours (768, md)
   * are 560 at 620 and 1024.
   */
  readonly maxWidth?: 'md' | 'lg';
  /**
   * The legacy card's own inline `max-width`, as a CSS length (B1286, B1243), e.g. IdVerificationForm's `width: 650px;
   * max-width: 80vw`. An inline max-width replaces Quasar's 560 cap at every width (measured: a 768 card given `max-width:
   * 80vw` is 496 at 620, 640 at 800 and 768 at 1024), and keeps its cap inside the maximized phone dialog: a full-height
   * card centred over the page.
   */
  readonly cardMaxWidth?: string;
  /** Disables the close button, as legacy's Loading.show() covers a dialog while it saves (B1324 review). */
  readonly closeDisabled?: boolean;
}

/**
 * How the dialog sits below 600 px (legacy's domStore.isMobile), B1246:
 * - 'maximized' (default): BaseDialog's `maximized` on mobile, the whole screen, or with `cardMaxWidth` a full-height card
 *   centred over the page.
 * - 'inset': a bare q-dialog around a UiCard or q-card (InviteUser, DrawerBreakdown, the No Location alert; RoleOrderDialog
 *   until legacy b4bfde902 moved it onto BaseDialog, B1747), which stays Quasar's minimized dialog: padded 24 all round, so the card is its own width up to 100vw - 48,
 *   centred both ways, at most 100vh - 48 tall. Measured on legacy GG LA at 390: Account User Invite and (before b4bfde902) Role
 *   Order are 342 wide at x 24, vertically centred. 'inset' also gives the card the bare q-dialog's 4 px radius at
 *   every width, not BaseDialog's 14 (B1321, B1330).
 */
type DialogProps = DialogBaseProps & {
  readonly phone?: 'maximized' | 'inset';
  /**
   * True (default) is legacy's persistent q-dialog and BaseDialog: a click on the backdrop never closes it, and Escape only
   * with `closeOnEscape`. False is a bare `<q-dialog>` without `persistent` (B1324: the No Location alert, AccountsList.vue
   * and AccountsDetail.vue), which a backdrop click or Escape closes; `closeOnEscape` is then
   * ignored, as Escape always closes it.
   */
  readonly persistent?: boolean;
  /**
   * Below 600 px, phone 'inset' only: the card's own width there when it differs from `width` (B1330), as legacy
   * UiDeleteDialog's and UiUnsavedChangesDialog's `width: domStore.isMobile ? '768px' : …`, still capped at 100vw - 48.
   */
  readonly phoneWidth?: number;
};

/**
 * The card from 600 px at every phone mode: Quasar's minimized q-dialog, padded 24 all round, so the card is its own width
 * up to 100vw - 48 and its cap (`--dialog-cap`), centred, at most 100vh - 48 tall (B1216, B1243).
 */
const MINIMIZED_FROM_600 = 'min-[600px]:inset-auto min-[600px]:top-1/2 min-[600px]:left-1/2 min-[600px]:max-h-[calc(100dvh-48px)] min-[600px]:w-[min(var(--dialog-w),calc(100vw-48px),var(--dialog-cap))] min-[600px]:-translate-x-1/2 min-[600px]:-translate-y-1/2';
/**
 * The card's corners (B1321). BaseDialog's scoped `.q-dialog__inner > div { border-radius: 14px }` rounds its card 14 from
 * 600 (maximized below, square). A bare q-dialog (phone 'inset') keeps Quasar's `.q-dialog__inner > div { border-radius:
 * $generic-border-radius }`, 4 px at every width, which outranks UiCard's `.rad-ui-card` 12 and a q-card's own 4.
 */
const RADIUS = { maximized: 'min-[600px]:rounded-dialog', inset: 'rounded-dialog-bare' } as const;
/** Quasar's 560 cap, lifted to 740 from 800 for BaseDialog's max-width="lg" (legacy @media 800); a cardMaxWidth replaces it inline (B1243). */
const CAP = { md: '[--dialog-cap:560px]', lg: '[--dialog-cap:560px] min-[800px]:[--dialog-cap:740px]' } as const;
/** Below 600 px, phone 'inset': still the minimized q-dialog, inset 24 all round and centred both ways (B1246). */
const PHONE_INSET = 'max-[600px]:top-1/2 max-[600px]:left-1/2 max-[600px]:max-h-[calc(100dvh-48px)] max-[600px]:w-[min(var(--dialog-w-phone,var(--dialog-w)),calc(100vw-48px),var(--dialog-cap))] max-[600px]:-translate-x-1/2 max-[600px]:-translate-y-1/2';
/**
 * Below 600 px (legacy's domStore.isMobile, width <= 599.99), maximized: a cardMaxWidth card is centred at full height, never
 * wider than the screen (the maximized q-dialog's card is `width: 100%`, so a 650px cap still fills a 390 phone).
 */
const PHONE_CARD_MAX = 'max-[600px]:right-auto max-[600px]:left-1/2 max-[600px]:w-[min(var(--dialog-w),100vw,var(--dialog-cap))] max-[600px]:-translate-x-1/2';
/** Below 600 px each footer button grows to an equal share of the row. */
const PHONE_FOOTER_FILL = 'max-[600px]:[&>*]:flex-1';

const FIELD_SELECTOR = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [role="combobox"]:not([aria-disabled="true"])';

/**
 * Legacy's `.ui-card-close-button` (UiCard, UiSideCard, UiCardAlternate: a dense round flat q-btn, icon="close"; B638),
 * measured on GG LA at 1600 px: 35 x 35, radius 8, Material's `close` in a 24 px box (the cross 14 px), the
 * `close-glyph` token (legacy's --q-text-tertiary #8c8d8c, kept under the AA default) on gray-50, and in the dark theme
 * $whiteHigh on the 12dp surface #313235 (`_dark.scss`). Hovering lays Quasar's focus helper over it at 15 %.
 */
export const DIALOG_CLOSE_CLASS = 'group relative inline-flex size-[35px] shrink-0 items-center justify-center rounded-[8px] bg-gray-50 text-close-glyph dark:bg-field';

/**
 * Quasar's `.q-focus-helper`: currentColor under its ::before (black at .1) and ::after (white at .4), shown at .15 on
 * hover and .22 on keyboard focus (focus-visible, so the focus Radix gives the button as a dialog opens never lights it),
 * fading as Quasar's does (.4s, cubic-bezier(.25,.8,.5,1)). The same mix as OUTLINE_HOVER's, laid over the button as a
 * layer rather than blended into its fill.
 */
const FOCUS_HELPER = 'bg-[color-mix(in_srgb,color-mix(in_srgb,currentColor_90%,black)_60%,white)]';

/** Material Icons `close`, which legacy's q-btn icon="close" draws, over the focus helper; for a button with DIALOG_CLOSE_CLASS. */
export function DialogCloseGlyph() {
  return (
    <>
      <span aria-hidden data-focus-helper className={`pointer-events-none absolute inset-0 rounded-[inherit] ${FOCUS_HELPER} opacity-0 transition-opacity duration-[400ms] ease-[cubic-bezier(.25,.8,.5,1)] group-hover:opacity-15 group-focus-visible:opacity-[0.22]`} />
      <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden data-testid="close-glyph" className="fill-current">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
      </svg>
    </>
  );
}

const closeButton = (
  <RadixDialog.Close aria-label="Close" className={DIALOG_CLOSE_CLASS}>
    <DialogCloseGlyph />
  </RadixDialog.Close>
);

/**
 * A legacy dialog card's shadow (B2013): Quasar's standard q-card shadow in light, and none in dark, where legacy's
 * `_dark.scss` sets `.q-dialog__inner .q-card--dark { box-shadow: none }` for UiCard dialogs, the plugin's cards and
 * UiSideCard sheets alike (B2019). The string equals Quasar's raised q-btn shadow (`RAISED` below) only by coincidence:
 * Quasar gives both the same elevation-2 shadow, so the two stay separate.
 */
const DIALOG_CARD_SHADOW = 'shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)] dark:shadow-none';
/**
 * Legacy UiCard's footer (B816; UiSideCard's too, B936): no rule in the light theme but a soft shadow cast up into the body
 * (`.card-action-section { box-shadow: 0 -1px 6px rgb(0 0 0 / 16%) }`, over the body as `.footer-wrapper`'s z-index), so
 * the footer is 52 px; the dark theme adds a 1 px rule on top (`body--dark .footer-wrapper { border-top }`).
 */
const DIALOG_FOOTER_EDGE = 'relative z-[1] shadow-[0_-1px_6px_rgb(0_0_0/16%)] dark:border-t dark:border-line';

export function Dialog({ open, onOpenChange, title, children, footer, width = backofficeMetrics.dialog.width, testId, initialFocus = 'close', closeOnEscape = false, phone = 'maximized', phoneFooterFill = false, maxWidth = 'md', cardMaxWidth, persistent = true, closeDisabled = false, phoneWidth }: DialogProps) {
  const placement = phone === 'inset' ? `fixed z-[2001] ${PHONE_INSET} ${RADIUS.inset}` : `fixed inset-0 z-[2001] ${RADIUS.maximized}${cardMaxWidth === undefined ? '' : ` ${PHONE_CARD_MAX}`}`;
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const focusTarget = useCallback(
    () => (initialFocus === 'first-field' ? bodyRef.current?.querySelector<HTMLElement>(FIELD_SELECTOR) : null) ?? closeRef.current,
    [initialFocus],
  );
  const focusOnOpen = (event: Event) => {
    event.preventDefault();
    focusTarget()?.focus();
  };
  // A field's `autoFocus` runs at commit, before Radix's open autofocus, and Radix then skips onOpenAutoFocus. The body's
  // ref is attached after its children commit, so a stray autoFocus is moved to the dialog's target here.
  // Stable, so it runs when the body mounts and never again while the user types in it.
  const attachBody = useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    if (node !== null && node.contains(document.activeElement)) focusTarget()?.focus();
  }, [focusTarget]);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[2000] bg-black/40" />
        <RadixDialog.Content
          data-testid={testId}
          // Persistent like q-dialog persistent: a click outside never discards the form. A non-persistent one (B1324)
          // closes on a backdrop click or Escape, as Quasar's plain q-dialog.
          onPointerDownOutside={(e) => { if (persistent) e.preventDefault(); }}
          onInteractOutside={(e) => { if (persistent) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (persistent && !closeOnEscape) e.preventDefault(); }}
          aria-describedby={undefined}
          onOpenAutoFocus={focusOnOpen}
          style={{ ['--dialog-w' as string]: `${width}px`, ...(cardMaxWidth === undefined ? {} : { ['--dialog-cap' as string]: cardMaxWidth }), ...(phoneWidth === undefined ? {} : { ['--dialog-w-phone' as string]: `${phoneWidth}px` }) }}
          // Below 600 the phone mode (fullscreen as BaseDialog's `maximized` on domStore.isMobile, or inset as a bare
          // q-dialog); from 600 Quasar's minimized q-dialog pads 24 px all round, so the card is its own width up to 100vw - 48 (B1216).
          className={`${placement} flex flex-col bg-dialog ${DIALOG_CARD_SHADOW} ${CAP[maxWidth]} ${MINIMIZED_FROM_600}`}
        >
          <div className="flex min-h-[68px] items-center justify-between gap-4 border-b border-line px-6">
            <RadixDialog.Title className="text-heading-sm font-bold text-text-1">{title}</RadixDialog.Title>
            <RadixDialog.Close ref={closeRef} aria-label="Close" disabled={closeDisabled} className={`${DIALOG_CLOSE_CLASS} disabled:cursor-not-allowed disabled:opacity-70`}>
              <DialogCloseGlyph />
            </RadixDialog.Close>
          </div>
          <div ref={attachBody} className="min-h-0 flex-1 overflow-y-auto px-6 py-6 min-[600px]:max-h-[65dvh]">{children}</div>
          {/* Legacy's footer is a q-card-actions, where `.q-card__actions .q-btn--rectangle { padding: 0 8px }` gives its
              buttons 8 px sides in place of q-btn's 16 (B983: Approve Swap 120 wide, not 125). */}
          {footer === undefined ? null : <div className={`flex items-center justify-between gap-2 px-[6px] py-[6px] min-[600px]:justify-end [&_button]:px-2 ${DIALOG_FOOTER_EDGE}${phoneFooterFill ? ` ${PHONE_FOOTER_FILL}` : ''}`}>{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface SideSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly testId?: string;
  /** `centered` (legacy order popup, DrawerOrderPopup): the close X at the left and the title bold in the middle. */
  readonly header?: 'default' | 'centered';
  /** Width in px from 600 up (default 584, W15 UiSideCard); legacy's order popup is 450. Full width below 600 (UiSideCard's `$breakpoint-xs-max`). */
  readonly width?: number;
  /** Escape leaves it open by default, as legacy's persistent q-dialog; true is the opt-in (legacy's non-persistent ones). */
  readonly closeOnEscape?: boolean;
  /** The body's padding classes (default `px-6 py-10`); legacy's UiSideCard pads its body 24 px all round (`p-6`). */
  readonly bodyClassName?: string;
}

/**
 * W15 UiSideCard: a right-hand slide-over (the timecard editor), 584 px wide unless given, full height. Both headers title
 * it as legacy does, `text-heading-sm text-weight-bold` (UiSideCard, DrawerOrderPopup and RefundDetailPopup alike).
 */
export function SideSheet({ open, onOpenChange, title, children, footer, testId, header = 'default', width = 584, closeOnEscape = false, bodyClassName = 'px-6 py-10' }: SideSheetProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[2000] bg-black/40" />
        <RadixDialog.Content
          data-testid={testId}
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => { if (!closeOnEscape) e.preventDefault(); }}
          style={{ ['--sheet-w' as string]: `${width}px` }}
          className={`fixed inset-y-0 right-0 z-[2001] flex w-full flex-col bg-dialog ${DIALOG_CARD_SHADOW} min-[600px]:w-[var(--sheet-w)]`}
        >
          {header === 'centered' ? (
            /* Legacy's q-card-section pads the 35 px close 16 all round: a 67 px header over the 1 px separator. */
            <div className="relative flex items-center border-b border-line p-4" data-testid="side-sheet-header">
              <span className="z-10 flex">{closeButton}</span>
              <RadixDialog.Title className="absolute inset-x-0 text-center text-heading-sm font-bold text-text-1">{title}</RadixDialog.Title>
            </div>
          ) : (
            <div className="flex min-h-[61px] items-center border-b border-line px-4">
              {/* Legacy's title sits in a row with the 35 px close and stretches to it, so its 32 px line starts 1.5 px above centre. */}
              <div className="flex flex-1 justify-between" data-testid="side-sheet-title-row">
                <RadixDialog.Title className="text-heading-sm font-bold text-text-1">{title}</RadixDialog.Title>
                {closeButton}
              </div>
            </div>
          )}
          <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>
          {/* Legacy UiSideCard's footer is UiCard's: the shadow cast up into the body, the rule only in dark (B936). */}
          {footer === undefined ? null : <div className={`flex items-center gap-3 p-[6px] ${DIALOG_FOOTER_EDGE}`}>{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface FullScreenDialogProps {
  readonly open: boolean;
  /** The X at the top left: the caller decides whether leaving needs a confirmation first. */
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
  /** The header's buttons, right-aligned (legacy UiCardAlternate header actions). */
  readonly actions?: ReactNode;
  readonly testId?: string;
  /** Escape leaves it open by default, as legacy's persistent q-dialog; true is the opt-in (legacy's non-persistent ones). */
  readonly closeOnEscape?: boolean;
  /** No body padding, as legacy's `card-padding="0px"` callers (the QR editors), whose panes run to the edges. */
  readonly flush?: boolean;
}

/**
 * Whether the window is at least `px` wide (a `min-width` media query that follows resizes). Without matchMedia (jsdom, a
 * server render) it answers true: the desktop layout.
 */
function useMinWidth(px: number): boolean {
  const query = `(min-width: ${String(px)}px)`;
  const read = (): boolean => typeof window === 'undefined' || typeof window.matchMedia !== 'function' || window.matchMedia(query).matches;
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(query);
    const update = () => { setWide(media.matches); };
    update();
    media.addEventListener('change', update);
    return () => { media.removeEventListener('change', update); };
  }, [query]);
  return wide;
}

/**
 * The maximized editor (legacy q-dialog maximized + UiCardAlternate: the item editor): a 60 px header with the close
 * button left, the title centred and the actions right; the body scrolls under it. Escape leaves it open (legacy's maximized dialogs are persistent) unless `closeOnEscape`, and then asks like
 * the X does. It sits
 * under the regular dialogs, so a dialog opened from it dims it.
 */
export function FullScreenDialog({ open, onClose, title, children, actions, testId, closeOnEscape = false, flush = false }: FullScreenDialogProps) {
  // Legacy hides the header's buttons below md (`gt-sm`) and shows them in its footer bar (B1078).
  const wide = useMinWidth(1024);
  const footed = !wide && actions !== undefined && actions !== null;
  return (
    <RadixDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <RadixDialog.Portal>
        <RadixDialog.Content
          data-testid={testId}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => { if (!closeOnEscape) e.preventDefault(); }}
          // Focus opens on the close X, as on every q-dialog (coordinator 2026-10-12), but without its ring until a key is
          // pressed: legacy's X opens unringed (lane V r20 saw a green ring on Payroll Summary's detail).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const close = (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>('[data-full-screen-close]');
            if (close !== null && close !== undefined) { close.dataset.quiet = ''; close.focus(); }
          }}
          onKeyDown={(e) => { (e.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-full-screen-close]')?.removeAttribute('data-quiet'); }}
          className="fixed inset-0 z-[1900] flex flex-col bg-dialog"
        >
          {/* Legacy UiCardAlternate: a 60 px title bar over its q-separator (rgba(0,0,0,.12)), the rule at y 60 (B1074). */}
          <div className="grid min-h-[61px] grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-black/[0.12] px-4 dark:border-line" data-testid="full-screen-header">
            <div>
              <button type="button" aria-label="Close" onClick={onClose} data-full-screen-close className={`${DIALOG_CLOSE_CLASS} data-[quiet]:outline-none`}>
                <DialogCloseGlyph />
              </button>
            </div>
            <RadixDialog.Title className="text-center text-heading-sm font-bold text-text-1">{title}</RadixDialog.Title>
            <div className="flex flex-wrap justify-end gap-2">{wide ? actions : null}</div>
          </div>
          {/* Legacy's .ui-body pads 74 24 36 under the fixed 60 px bar, so content starts 13 px under the rule (B1077). */}
          <div className={flush ? 'min-h-0 flex-1 overflow-y-auto' : 'min-h-0 flex-1 overflow-y-auto px-6 pt-[13px] pb-9'}>{children}</div>
          {/* Legacy's footer-wrapper below md: a q-card-actions bar at the bottom, 6 px in, Quasar's 0 -1px 6px shadow, its
              buttons padded 0 8 (.q-card__actions .q-btn--rectangle). Under 600 they are BaseButton `full` (width 100 %,
              shrinking to fit-content) 8 px apart (q-btn + q-btn); from 600 they sit right, 13 px apart (gap 5 + 8; B1078). */}
          {footed ? (
            <div className="flex shrink-0 items-center justify-between gap-2 bg-dialog p-[6px] shadow-[0_-1px_6px_rgba(0,0,0,0.16)] *:px-2 max-[600px]:*:w-full max-[600px]:*:min-w-fit min-[600px]:justify-end min-[600px]:gap-[13px]" data-testid="full-screen-footer">
              {actions}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface ConfirmCardProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: ReactNode;
  /** The filled primary button at the right (Quasar's `ok`); it takes focus on open, as the plugin's `focus: 'ok'`. */
  readonly confirmLabel: string;
  /** The flat primary button left of it (Quasar's `cancel`); omitted, the card has the confirm only (`cancel: false`, B1997). */
  readonly cancelLabel?: string | undefined;
  readonly onConfirm: () => void;
  /** Needed with a `cancelLabel`. */
  readonly onCancel?: (() => void) | undefined;
  readonly testId?: string | undefined;
  /** Both buttons' `noCaps`: the labels as written, not upper-cased (DepartmentList.vue's Remove All Items, B1333). */
  readonly noCaps?: boolean;
  /**
   * Both buttons given as `{ unelevated: true }` objects: Quasar then drops the cancel's default `flat`, so both are filled
   * primary with no raised shadow (measured on legacy GG LA's Remove All Items: both rgb(77, 131, 61), B1333).
   */
  readonly unelevated?: boolean;
  /** The confirm's colour (Quasar's `ok: { color }`): primary, or negative for a delete (Employee Breaks' in-use delete, B1571). */
  readonly confirmTone?: 'primary' | 'negative';
  /** Quasar's `options` (a checkbox or radio group) under the message, in its own section; the confirm then takes no focus. */
  readonly children?: ReactNode;
}

/** Quasar's `.q-dialog__inner > .q-card > .q-card__actions .q-btn--rectangle { min-width: 64px }` (B1333: Yes is 64 wide in legacy). */
const CARD_BUTTON = 'group relative inline-flex min-w-16 h-9 items-center justify-center rounded-q px-2 text-[14px] leading-6 font-normal tracking-[0.25px] uppercase outline-none';
/** Quasar's q-focus-helper: the button's own colour over it, 15 % on hover and 22 % while focused (legacy focuses the confirm). */
const focusHelper = <span aria-hidden data-focus-helper className="pointer-events-none absolute inset-0 rounded-[inherit] bg-current opacity-0 transition-opacity group-hover:opacity-15 group-focus:opacity-[0.22]" />;
/** Quasar's standard (raised) q-btn shadow, which the plugin's filled confirm carries. */
const RAISED = 'shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)]';

/**
 * Legacy's Quasar `Dialog.create({ title, message, ok, cancel, persistent })` card (the "Apply Role Permissions?" prompt),
 * measured live 2026-10-14: 400 px wide, radius 4, Quasar's card shadow on the 40 % backdrop, centred. The title is 20/32
 * px 500 in --q-text-secondary (#646866 light, #ffffffb3 dark; #1646 review) with 16 px padding, and the message is 14/21 px under it (padding 0 16 16). No close button and no
 * dividers. The actions sit right, 6 px in, with 36 px buttons padded 0 8 px, radius 8, upper-cased 14 px 400: the cancel
 * flat in primary, then the confirm filled, 8 px apart. Persistent: neither Escape nor a click outside closes it.
 * On a phone the card shrinks inside Quasar's minimized dialog inner, padded 24 px, so it is 100vw - 48 wide (342 at 390;
 * B1336), where a 95vw cap gave 370.
 */
export function ConfirmCard({ open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, testId, noCaps = false, unelevated = false, confirmTone = 'primary', children }: ConfirmCardProps) {
  const button = noCaps ? CARD_BUTTON.replace(' uppercase', '') : CARD_BUTTON;
  const confirmRef = useRef<HTMLButtonElement>(null);
  return (
    <RadixDialog.Root open={open}>
      <RadixDialog.Portal>
        {/* Above a Dialog's content (z-2001), so the dialog it was opened from dims too, as under legacy's second backdrop. */}
        <RadixDialog.Overlay className="fixed inset-0 z-[2010] bg-black/40" data-testid="confirm-card-backdrop" />
        <RadixDialog.Content
          data-testid={testId}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          // With options, Quasar's plugin focuses neither button: the dialog itself holds focus (measured on legacy, B1571).
          onOpenAutoFocus={(e) => { e.preventDefault(); if (children === undefined) confirmRef.current?.focus(); else (e.currentTarget as HTMLElement | null)?.focus(); }}
          className={`fixed top-1/2 left-1/2 z-[2011] flex w-[400px] max-w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[4px] bg-dialog tracking-[0.25px] text-text-2 ${DIALOG_CARD_SHADOW}`}
        >
          <RadixDialog.Title className="p-4 text-[20px] leading-8 font-medium" data-testid="confirm-card-title">{title}</RadixDialog.Title>
          <div className="px-4 pb-4 text-[14px] leading-[21px]" data-testid="confirm-card-message">{message}</div>
          {/* Quasar's plugin form: a q-separator (rgba(0,0,0,.12)) above and below a 16 px padded section (measured on legacy's
              Employee Breaks in-use delete, B1571). */}
          {children === undefined ? null : <div className="border-y border-black/[0.12] p-4 dark:border-line" data-testid="confirm-card-options">{children}</div>}
          <div className="flex items-center justify-end gap-2 p-1.5" data-testid="confirm-card-actions">
            {cancelLabel === undefined ? null : <button type="button" onClick={onCancel} className={`${button} ${unelevated ? 'bg-primary text-white dark:text-text-inverted' : 'bg-transparent text-primary'}`}>{focusHelper}{cancelLabel}</button>}
            <button type="button" ref={confirmRef} onClick={onConfirm} className={`${button}${unelevated ? '' : ` ${RAISED}`} ${confirmTone === 'negative' ? 'bg-negative' : 'bg-primary'} text-white dark:text-text-inverted`}>{focusHelper}{confirmLabel}</button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly testId?: string | undefined;
  /** The card's width from 600 px: legacy UiUnsavedChangesDialog's `width` prop, 404 by default (B1332). */
  readonly width?: number;
}

/**
 * Legacy UiUnsavedChangesDialog: the message, then a flat tertiary "Discard Changes" left and the primary "Save Changes"
 * right, each taking half the footer (also "Restore Default Modifier Set": Cancel / Proceed).
 */
export function ConfirmDialog({ open, onOpenChange, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, testId, width = backofficeMetrics.dialog.unsavedChangesWidth }: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      // Legacy UiUnsavedChangesDialog's q-card is `width: props.width`, '404px' unless the caller says otherwise, where
      // UiDeleteDialog's is 450 (B1332).
      width={width}
      testId={testId}
      // A bare persistent q-dialog around a q-card (UiUnsavedChangesDialog), so inset and 4 px round, 768 wide below 600 (B1330).
      phone="inset"
      phoneWidth={768}
      footer={
        <div className="flex w-full gap-2">
          <Button variant="flat" className="flex-1 !text-text-3" onClick={onCancel}>{cancelLabel}</Button>
          <Button className="flex-1" onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      }
    >
      <div className="text-[14px] text-text-1">{message}</div>
    </Dialog>
  );
}

interface DeleteDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  /** What is deleted, in bold in the legacy sentence. */
  readonly model: string;
  readonly onConfirm: () => void;
  readonly busy?: boolean;
  readonly illustration?: string;
  /** The picture in dark mode (legacy UiDeleteDialog swaps in BO_DELETE_CONFIRMATION_DARK, B1569); the light one shows when absent. */
  readonly darkIllustration?: string | undefined;
  /** A sentence of its own in place of the standard one (legacy `deleteDialog(title, message, …)`). */
  readonly message?: ReactNode | undefined;
  /** Legacy UiDeleteDialog's `heading`: a line over the message (the "Whoops! There appears to be a problem." of a blocked delete). */
  readonly heading?: string | undefined;
  /** The confirm button's label (legacy `okButtonLabel`), Delete when absent. */
  readonly confirmLabel?: string | undefined;
  readonly children?: ReactNode;
}

export function DeleteDialog({ open, onOpenChange, title, model, onConfirm, busy, illustration, darkIllustration, message, heading, confirmLabel, children }: DeleteDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width={backofficeMetrics.dialog.deleteWidth}
      testId="delete-dialog"
      // UiDeleteDialog is a bare persistent q-dialog around a q-card: inset 24 on a phone (768 capped to 100vw - 48, 342 at
      // 390, 551 at 599) and 4 px round at every width, measured on legacy GG LA's Delete Category at 390 to 1024 (B1330).
      phone="inset"
      phoneWidth={768}
      // Below 600 both BaseButtons are `:full` in a no-wrap row: Cancel takes what the confirm label leaves (130 of 326 at 390).
      phoneFooterFill
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {/* Its label never wraps (legacy measured 192 wide for "View items in this category" at 390). */}
          <Button variant="negative" className="whitespace-nowrap" loading={busy ?? false} onClick={onConfirm}>{confirmLabel ?? 'Delete'}</Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        {illustration === undefined ? null : <img src={illustration} alt="" width={150} height={130} className={darkIllustration === undefined ? undefined : 'dark:hidden'} data-testid="delete-dialog-picture" />}
        {illustration === undefined || darkIllustration === undefined ? null : <img src={darkIllustration} alt="" width={150} height={130} className="hidden dark:block" data-testid="delete-dialog-picture-dark" />}
        {/* Legacy UiDeleteDialog: the illustration centred, the heading (text-heading-sm) and sentence left-aligned under it. */}
        {heading === undefined ? null : <h3 className="self-stretch text-left text-heading-sm font-medium text-text-2">{heading}</h3>}
        <p className="self-stretch text-left text-[14px] text-text-2">
          {message ?? <>Are you sure you want to delete this <b className="text-text-1">{model}</b>? This action cannot be undone.</>}
        </p>
        {children}
      </div>
    </Dialog>
  );
}

type ToastKind = 'positive' | 'negative' | 'warning';
interface ToastItem { readonly id: number; readonly kind: ToastKind; readonly message: string; }

interface ToastApi {
  readonly success: (message: string) => void;
  readonly error: (message: string) => void;
  readonly warning: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATION: Readonly<Record<ToastKind, number>> = {
  positive: backofficeMetrics.toast.successMs,
  // IMPROVE-BO-01 proposes 4 s errors (legacy 800 ms is hard to read); errors stay on screen long enough to read.
  negative: backofficeMetrics.toast.warningMs,
  warning: backofficeMetrics.toast.warningMs,
};

const TONE: Readonly<Record<ToastKind, string>> = {
  positive: 'bg-positive text-white dark:text-text-inverted',
  negative: 'bg-negative text-white dark:text-text-inverted',
  warning: 'bg-warning text-black',
};

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((kind: ToastKind, message: string) => {
    setItems((current) => [...current, { id: Date.now() + current.length, kind, message }]);
  }, []);
  const api = useMemo<ToastApi>(() => ({
    success: (m) => push('positive', m), error: (m) => push('negative', m), warning: (m) => push('warning', m),
  }), [push]);
  return (
    <ToastContext.Provider value={api}>
      <Toast.Provider swipeDirection="down">
        {children}
        {items.map((t) => (
          <Toast.Root
            key={t.id}
            duration={DURATION[t.kind]}
            type={t.kind === 'negative' ? 'foreground' : 'background'}
            onOpenChange={(open) => { if (!open) setItems((current) => current.filter((x) => x.id !== t.id)); }}
            className={`relative overflow-hidden rounded-[4px] px-4 py-3 text-[14px] shadow-lg ${TONE[t.kind]}`}
            data-testid={`toast-${t.kind}`}
          >
            <Toast.Description>{t.message}</Toast.Description>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed bottom-4 left-1/2 z-[4000] flex w-[min(560px,calc(100vw-32px))] -translate-x-1/2 flex-col gap-2 outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast needs a <ToastProvider>');
  return api;
}
